import React, { FC, useCallback, useMemo } from 'react';
import Chip from '@mui/material/Chip';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import CheckBoxOutlineBlankSharpIcon from '@mui/icons-material/CheckBoxOutlineBlankSharp';
import CheckBoxSharpIcon from '@mui/icons-material/CheckBoxSharp';
import styled from '@mui/styles/styled';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TextInputController } from '../InputControllers/TextInputController';
import {
  getEffectiveDailyPrice,
  getPackageDurationDays,
  PackageDuration,
  packageDurations,
} from '../../models/PackageDuration';

interface IProps {
  form: UseFormReturn;
}

// MUI's own chip metrics are too tight for a click target this size, so the box, the label
// padding and the icon inset are all widened. The radius tracks the height to stay a pill.
const durationChipSx = {
  height: 40,
  borderRadius: '20px',
  '& .MuiChip-label': {
    paddingLeft: 2,
    paddingRight: 2,
  },
  '& .MuiChip-icon': {
    marginLeft: 1.5,
  },
};

const ChipsRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

// InputAdornment renders string children as `text.secondary`, which this theme shows almost
// as bright as the value itself. A Typography child opts out of that wrapper.
const MutedAdornment: FC<{ position: 'start' | 'end' }> = ({ position, children }) => (
  <InputAdornment
    position={position}
    // The filled variant only offsets `start` adornments to clear the floating label, so an
    // `end` one stays centred in the full field height instead of sitting on the value line.
    // The extra left margin keeps it clear of the number input's spinner, which sits flush
    // against it once the filled variant drops the input's right padding.
    sx={position === 'end' ? { marginTop: 2, marginLeft: 2 } : undefined}
  >
    <Typography color="text.disabled">{children}</Typography>
  </InputAdornment>
);

const DiscountCell = styled(TableCell)({
  width: 160,
  // The theme gives every form control a top margin, which would push the input out of the row.
  '& .MuiFormControl-root': {
    marginTop: 0,
  },
});

// Kept inside the cell rather than on it, so the cell stays a table-cell and the columns line up.
const CustomDurationField = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.spacing(1),
  // The theme gives every form control a top margin, which would push the input out of the row.
  '& .MuiFormControl-root': {
    marginTop: 0,
    width: 96,
  },
}));

const MutedCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.text.disabled,
}));

const TotalCell = styled(TableCell)({
  fontWeight: 600,
  // The theme gives every form control a top margin, which would push the input out of the row.
  '& .MuiFormControl-root': {
    marginTop: 0,
    width: 140,
  },
});

const MutedValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.disabled,
}));

export const PackageDurationPricing: FC<IProps> = ({ form }) => {
  const { t } = useTranslation();

  const selected: PackageDuration[] = form.watch('durations') ?? [];
  const customDurationDays = form.watch('customDurationDays');
  const baseDailyPrice = Number(form.watch('baseDailyPrice')) || 0;
  const discounts: Partial<Record<PackageDuration, number | string>> = form.watch('discounts') ?? {};

  const priceFormatter = useMemo(() => Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }), []);

  const toggleDuration = useCallback(
    (duration: PackageDuration) => () => {
      form.setValue(
        'durations',
        selected.includes(duration) ? selected.filter((item) => item !== duration) : [...selected, duration]
      );
    },
    [form, selected]
  );

  const rows = useMemo(() => packageDurations.filter((duration) => selected.includes(duration)), [selected]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={4}>
        <TextInputController
          control={form.control}
          errors={form.formState.errors}
          label={t('adminProducts.packages.form.baseDailyPrice')}
          name="baseDailyPrice"
          inputType="number"
          InputProps={{
            startAdornment: <MutedAdornment position="start">$</MutedAdornment>,
            endAdornment: <MutedAdornment position="end">{t('adminProducts.packages.form.perDay')}</MutedAdornment>,
            inputProps: {
              min: 0,
            },
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <FormLabel>{t('adminProducts.packages.form.durationVariations')}:</FormLabel>
      </Grid>

      <Grid item xs={12}>
        <ChipsRow>
          {packageDurations.map((duration) => {
            const isSelected = selected.includes(duration);
            return (
              <Chip
                key={duration}
                // The theme defaults chips to `small`, which is too cramped for a click target.
                size="medium"
                sx={durationChipSx}
                icon={isSelected ? <CheckBoxSharpIcon /> : <CheckBoxOutlineBlankSharpIcon />}
                label={t(`adminProducts.packages.form.durations.${duration}`)}
                variant="outlined"
                color={isSelected ? 'primary' : 'default'}
                onClick={toggleDuration(duration)}
              />
            );
          })}
        </ChipsRow>
      </Grid>

      {rows.length ? (
        <>
          <Grid item xs={12}>
            <FormLabel sx={{ color: 'text.disabled' }}>{t('adminProducts.packages.form.tieredDiscounts')}</FormLabel>
          </Grid>

          <Grid item xs={12}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('adminProducts.packages.form.tieredDiscountsTable.duration')}</TableCell>
                  <TableCell>{t('adminProducts.packages.form.tieredDiscountsTable.discount')}</TableCell>
                  <TableCell>{t('adminProducts.packages.form.tieredDiscountsTable.effectiveDailyPrice')}</TableCell>
                  <TableCell>{t('adminProducts.packages.form.tieredDiscountsTable.totalPrice')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((duration) => {
                  const discount = Number(discounts[duration]) || 0;
                  const effectiveDailyPrice = getEffectiveDailyPrice(baseDailyPrice, discount);
                  const days = getPackageDurationDays(duration, customDurationDays);
                  const isPerpetual = duration === PackageDuration.Perpetual;

                  return (
                    <TableRow key={duration}>
                      <TableCell>
                        {duration === PackageDuration.Custom ? (
                          <CustomDurationField>
                            <TextInputController
                              control={form.control}
                              errors={form.formState.errors}
                              label=""
                              name="customDurationDays"
                              inputType="number"
                              size="small"
                              // The API rejects a custom variant without a positive day count.
                              // Unregistered on unmount so deselecting the chip clears the rule.
                              rules={{ required: true, min: 1 }}
                              shouldUnregister
                              InputProps={{
                                inputProps: {
                                  'aria-label': t('adminProducts.packages.form.customDurationDays'),
                                  min: 1,
                                },
                              }}
                            />
                            <Typography variant="body2" color="text.disabled">
                              {t('adminProducts.packages.form.days')}
                            </Typography>
                          </CustomDurationField>
                        ) : (
                          t(`adminProducts.packages.form.durations.${duration}`)
                        )}
                      </TableCell>
                      <DiscountCell>
                        {isPerpetual ? (
                          <MutedValue variant="body2">—</MutedValue>
                        ) : (
                          <TextInputController
                            control={form.control}
                            errors={form.formState.errors}
                            label=""
                            name={`discounts.${duration}`}
                            inputType="number"
                            size="small"
                            InputProps={{
                              inputProps: {
                                'aria-label': t('adminProducts.packages.form.tieredDiscountsTable.discount'),
                                min: 0,
                                max: 100,
                              },
                            }}
                          />
                        )}
                      </DiscountCell>
                      <MutedCell>{days === null ? '—' : priceFormatter.format(effectiveDailyPrice)}</MutedCell>
                      <TotalCell>
                        {isPerpetual ? (
                          // Perpetual has no day count to multiply, so the total is entered rather than derived.
                          <TextInputController
                            control={form.control}
                            errors={form.formState.errors}
                            label=""
                            name="perpetualTotalPrice"
                            inputType="number"
                            size="small"
                            InputProps={{
                              startAdornment: <MutedAdornment position="start">$</MutedAdornment>,
                              inputProps: {
                                'aria-label': t('adminProducts.packages.form.tieredDiscountsTable.totalPrice'),
                                min: 0,
                              },
                            }}
                          />
                        ) : (
                          <>{days === null ? '—' : priceFormatter.format(effectiveDailyPrice * days)}</>
                        )}
                      </TotalCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Grid>
        </>
      ) : null}
    </Grid>
  );
};
